import { GasStationClient } from "@aptos-labs/gas-station-client";
import {
  AccountAuthenticator,
  PendingTransactionResponse,
  SimpleTransaction,
  TransactionResponseType,
} from "@aptos-labs/ts-sdk";

import { DecibelConfig } from "./constants";

/**
 * Submit a transaction with Gas Station fee sponsorship.
 * Requires `gasStationApiKey` in the config.
 *
 * @param config The Decibel configuration (must include gasStationApiKey)
 * @param transaction The transaction to submit
 * @param senderAuthenticator The sender's authenticator
 * @returns A promise that resolves to the pending transaction response
 */
export async function submitFeePaidTransaction(
  config: DecibelConfig,
  transaction: SimpleTransaction,
  senderAuthenticator: AccountAuthenticator,
): Promise<PendingTransactionResponse> {
  if (!config.gasStationApiKey) {
    throw new Error("gasStationApiKey is required for fee-paid transactions");
  }

  const gasStationClient = new GasStationClient({
    network: config.network,
    apiKey: config.gasStationApiKey,
    // Use gasStationUrl as base URL for custom networks like localnet
    ...(config.gasStationUrl && { baseUrl: config.gasStationUrl }),
  });

  const { transactionHash } = await gasStationClient.signAndSubmitTransaction({
    transaction,
    senderAuthenticator,
  });

  // Build PendingTransactionResponse from SimpleTransaction.rawTransaction
  const rawTxn = transaction.rawTransaction;
  return {
    type: TransactionResponseType.Pending,
    hash: transactionHash,
    sender: rawTxn.sender.toString(),
    sequence_number: rawTxn.sequence_number.toString(),
    max_gas_amount: rawTxn.max_gas_amount.toString(),
    gas_unit_price: rawTxn.gas_unit_price.toString(),
    expiration_timestamp_secs: rawTxn.expiration_timestamp_secs.toString(),
    // Payload not available from gas station response
    payload: {} as PendingTransactionResponse["payload"],
  };
}
